import MenuIcon from '@mui/icons-material/Menu'
import {
  Box,
  IconButton,
  AppBar as MuiAppBar,
  AppBarProps as MuiAppBarProps,
  Toolbar,
  styled,
} from '@mui/material'
import { DatePreference } from '@npm-client-frontends/client-frontends-shared-types'
import {
  DATE_PREFERENCE_ATTRIBUTE,
  useUserAttributes,
} from '@npm-client-frontends/global-context'
import { AUDIENCE_COLORS } from '@npm-client-frontends/shared-constants'
import { useUserCognitoGroups } from '@npm-client-frontends/shared-hooks'
import {
  AudienceGroups,
  CognitoGroups,
} from '@npm-client-frontends/client-frontends-shared-types'
import { updateUserAttributes } from 'aws-amplify/auth'
import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import Logo from '../../../assets/images/npm-logo-blue.png'
import { env } from '../../environments/environment'
import { AudienceInfoButton } from '../AudienceInfoButton'
import ClaimsToggle from './ClaimsToggle'
import DateFormatModal from './DateFormatModal'
import { HeaderMenuButton } from './HeaderMenuButton'
import { SearchInput } from './SearchInput'

interface AppBarProps extends MuiAppBarProps {
  open?: boolean
}

const handleUpdateDateFormat = async (
  dateFormat: DatePreference,
  modalAction: (open: boolean) => void,
) => {
  try {
    const result = await updateUserAttributes({
      userAttributes: {
        [DATE_PREFERENCE_ATTRIBUTE]: dateFormat,
      },
    })
    modalAction(false)

    if (result[DATE_PREFERENCE_ATTRIBUTE].isUpdated === true) {
      window.location.reload()
    }
  } catch (error) {
    console.error('Error updating user attributes', error)
  }
}

const AppBar = styled(MuiAppBar)<AppBarProps>(({ theme }) => ({
  backgroundColor: '#303c64',
  zIndex: theme.zIndex.drawer + 2,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
}))

const StyledLogo = styled('img')(() => ({
  height: 40,
  width: 'auto',
}))

export interface ContentSet {
  audienceGroup: AudienceGroups
  title: string
  color: string
  abbreviation: string
}

export const getContentSets = (
  userCognitoGroups: CognitoGroups[],
): ContentSet[] => {
  const hasNARegion = userCognitoGroups.includes(CognitoGroups.RegionNA)
  const hasEURRegion = userCognitoGroups.includes(CognitoGroups.RegionEU)

  const contentSets = [
    {
      audienceGroup: CognitoGroups.ContentUtilityScale,
      title: 'US Utility-Scale',
      color: AUDIENCE_COLORS.UTILITY_SCALE,
      abbreviation: 'UT',
    },
    {
      audienceGroup: CognitoGroups.ContentDistributedGeneration,
      title: 'US Distributed Generation',
      color: AUDIENCE_COLORS.DISTRIBUTED_GENERATION,
      abbreviation: 'DG',
    },
    {
      audienceGroup: CognitoGroups.ContentDataCenters,
      title: `${hasNARegion && hasEURRegion ? 'US/EUR' : hasEURRegion ? 'EUR' : 'US'} Data Centers`,
      color: AUDIENCE_COLORS.DATA_CENTERS,
      abbreviation: 'DC',
    },
    {
      audienceGroup: CognitoGroups.ContentEurope,
      title: 'Europe Renewables',
      color: AUDIENCE_COLORS.EUROPE,
      abbreviation: 'EUR',
    },
  ]

  if (!env.features.enableEuropeRegion) {
    contentSets.pop()
  }

  return contentSets
}

export const Header = ({
  toggleVisibility,
}: {
  toggleVisibility: () => void
}) => {
  const location = useLocation()
  const { userAudienceGroups, userCognitoGroups } = useUserCognitoGroups()

  const [openModal, setOpenModal] = useState(false)
  const { userDateFormat, userId } = useUserAttributes()

  const contentSets = useMemo(
    () => getContentSets(userCognitoGroups),
    userCognitoGroups,
  )

  useEffect(() => {
    if (userDateFormat === DatePreference.NONE) {
      // eslint-disable-next-line no-console
      console.info(`Date preference modal triggered for user: ${userId}`)
      setOpenModal(true)
    }
  }, [userDateFormat])

  const shouldRenderDateFormatModal = location.pathname !== '/login'

  return (
    <AppBar position="fixed">
      <Toolbar>
        <IconButton
          data-testid="toggle-sidebar-button"
          color="inherit"
          onClick={toggleVisibility}
          edge="start"
          sx={{
            marginRight: 3,
          }}
        >
          <MenuIcon />
        </IconButton>

        <Box
          sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, gap: 1 }}
        >
          <StyledLogo src={Logo} alt="Logo" />
          <ClaimsToggle
            userAudienceGroups={userAudienceGroups}
            contentSets={contentSets}
          />
          <AudienceInfoButton
            userAudienceGroups={userAudienceGroups}
            contentSets={contentSets}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 0.5 }}>
          <SearchInput />
          <HeaderMenuButton />
        </Box>
      </Toolbar>

      {shouldRenderDateFormatModal && (
        <DateFormatModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          onUpdateDateFormat={handleUpdateDateFormat}
        />
      )}
    </AppBar>
  )
}
